import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadForm from './pages/LeadForm';
import LeadDetail from './pages/LeadDetail';
import LeadImport from './pages/LeadImport';
import Pipeline from './pages/Pipeline';
import Payouts from './pages/Payouts';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Nonprofits from './pages/Nonprofits';
import Vendors from './pages/Vendors';
import CommissionStructure from './pages/CommissionStructure';
import TermsOfUse from './pages/TermsOfUse';
import TenantSignup from './pages/TenantSignup';
import PlatformDashboard from './pages/PlatformDashboard';
import PlatformSetup from './pages/PlatformSetup';
import Settings from './pages/Settings';
import './App.css';

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles, requirePlatformOwner }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requirePlatformOwner && !user.is_platform_owner) {
    return <Navigate to="/dashboard" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route wrapper (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Protected Routes - All Roles */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <Leads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/new"
        element={
          <ProtectedRoute>
            <LeadForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <ProtectedRoute>
            <LeadDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id/edit"
        element={
          <ProtectedRoute>
            <LeadForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/import"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'BackOffice']}>
            <LeadImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payouts"
        element={
          <ProtectedRoute>
            <Payouts />
          </ProtectedRoute>
        }
      />

      {/* Pipeline - All Roles (reps see their assigned leads only) */}
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <Pipeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'BackOffice']}>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nonprofits"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'BackOffice']}>
            <Nonprofits />
          </ProtectedRoute>
        }
      />

      {/* Admin Only Routes */}
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <Vendors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/commission-structure"
        element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <CommissionStructure />
          </ProtectedRoute>
        }
      />

      {/* Platform Owner Only Routes */}
      <Route
        path="/platform"
        element={
          <ProtectedRoute requirePlatformOwner>
            <PlatformDashboard />
          </ProtectedRoute>
        }
      />

      {/* Settings - All authenticated users */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Public Routes */}
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/signup" element={<TenantSignup />} />
      <Route path="/setup" element={<PlatformSetup />} />

      {/* Landing Page - Root */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
